import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { PlatformGlyph, IconPlug, IconCheck, IconExternal, IconTrash, IconKey } from '../lib/icons';

export default function Accounts({ platforms, onRefresh }) {
  const [accounts, setAccounts] = useState([]);
  const [connecting, setConnecting] = useState(null);
  const [label, setLabel] = useState('');
  const [username, setUsername] = useState('');
  const [profile, setProfile] = useState('');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const mounted = useRef(true);
  const messageTimer = useRef(null);

  const loadAccounts = useCallback(async () => {
    try {
      const data = await api.get('/accounts');
      if (mounted.current) setAccounts(data);
    } catch (err) {
      if (mounted.current) setAccounts([]);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadAccounts();
    return () => {
      mounted.current = false;
      if (messageTimer.current) clearTimeout(messageTimer.current);
    };
  }, [loadAccounts]);

  const show = (type, text) => {
    setMessage({ type, text });
    if (messageTimer.current) clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => {
      if (mounted.current) setMessage(null);
    }, 6000);
  };

  const refresh = () => {
    loadAccounts();
    onRefresh();
  };

  const connect = async (platformId) => {
    if (!label.trim() && !username.trim()) {
      show('err', 'Informe ao menos o nome da conta.');
      return;
    }
    setBusy(platformId);
    try {
      await api.post('/accounts', {
        platform: platformId,
        label: label || undefined,
        username: username || undefined,
        profile: profile || undefined,
      });
      setConnecting(null);
      setLabel('');
      setUsername('');
      setProfile('');
      show('ok', 'Conta conectada com sucesso.');
      refresh();
    } catch (err) {
      show('err', err.message);
    } finally {
      setBusy('');
    }
  };

  const disconnect = async (accountId) => {
    setBusy(accountId);
    try {
      await api.del(`/accounts/${accountId}`);
      show('ok', 'Conta desconectada.');
      refresh();
    } catch (err) {
      show('err', err.message);
    } finally {
      setBusy('');
    }
  };

  const test = async (accountId) => {
    setBusy(accountId);
    try {
      const res = await api.post(`/accounts/${accountId}/test`, {});
      show('ok', res.message || 'OK');
      refresh();
    } catch (err) {
      show('err', err.message);
    } finally {
      setBusy('');
    }
  };

  const accountsOf = (platformId) => accounts.filter((a) => a.platform === platformId);

  return (
    <div>
      {message ? <div className={`banner ${message.type}`}>{message.text}</div> : null}

      <div className="grid">
        {platforms.map((p) => {
          const own = accountsOf(p.id);
          return (
            <div className="plat-card" key={p.id}>
              <div className="plat-head">
                <div className="plat-logo" style={{ background: p.color }}>
                  <PlatformGlyph id={p.id} size={22} />
                </div>
                <div>
                  <div className="plat-name">{p.name}</div>
                  <div className="plat-sub">Aceita: {p.accepts.join(', ')}</div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <span className={`badge ${own.length ? 'ok' : 'muted'}`}>
                    {own.length ? `${own.length} conectada(s)` : 'Sem conta'}
                  </span>
                </div>
              </div>

              <div className="hint">{p.hint}</div>

              <div className="hint" style={{ marginTop: 8 }}>
                Modo atual: <strong>{p.mode === 'live' ? 'ao vivo' : 'demo (simulação)'}</strong>
              </div>

              {own.length > 0 ? (
                <div className="list" style={{ marginTop: 12 }}>
                  {own.map((acc) => (
                    <div className="row" key={acc.id} style={{ padding: '10px 12px' }}>
                      <div>
                        <div className="title">{acc.label || acc.username || 'Conta'}</div>
                        <div className="meta">
                          {acc.username || acc.id}
                          {acc.profile ? ` · perfil: ${acc.profile}` : ''}
                        </div>
                      </div>
                      <div className="actions">
                        <button
                          type="button"
                          className="btn ghost small"
                          disabled={busy === acc.id}
                          onClick={() => test(acc.id)}
                        >
                          <span className="btn-ico">
                            <IconKey size={14} />
                          </span>
                          Testar
                        </button>
                        <button
                          type="button"
                          className="btn danger small"
                          disabled={busy === acc.id}
                          onClick={() => disconnect(acc.id)}
                        >
                          <span className="btn-ico">
                            <IconTrash size={14} />
                          </span>
                          Desconectar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {connecting === p.id ? (
                <div className="connect-form">
                  <div className="field">
                    <label>Nome da conta</label>
                    <input
                      className="input"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder={`Ex.: ${p.name} - Loja`}
                    />
                  </div>
                  <div className="field">
                    <label>Usuário / identificador</label>
                    <input
                      className="input"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="@seu_usuario"
                      autoComplete="off"
                    />
                  </div>
                  <div className="field">
                    <label>Perfil do navegador (opcional)</label>
                    <input
                      className="input"
                      value={profile}
                      onChange={(e) => setProfile(e.target.value)}
                      placeholder="Ex.: Chrome - Perfil 1"
                      autoComplete="off"
                    />
                  </div>
                  <div className="hint" style={{ marginBottom: 10 }}>
                    Dica: cada conta em um perfil diferente do navegador mantém todas logadas ao mesmo tempo.
                  </div>
                  <div className="plat-actions">
                    <button
                      type="button"
                      className="btn primary small"
                      disabled={busy === p.id}
                      onClick={() => connect(p.id)}
                    >
                      <span className="btn-ico">
                        <IconCheck size={14} />
                      </span>
                      {busy === p.id ? 'Conectando…' : 'Confirmar'}
                    </button>
                    <button type="button" className="btn ghost small" onClick={() => setConnecting(null)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="plat-actions">
                  <button
                    type="button"
                    className="btn primary small"
                    onClick={() => {
                      setConnecting(p.id);
                      setLabel('');
                      setUsername('');
                      setProfile('');
                    }}
                  >
                    <span className="btn-ico">
                      <IconPlug size={14} />
                    </span>
                    Conectar
                  </button>
                  <a className="btn ghost small" href={p.docs} target="_blank" rel="noopener noreferrer">
                    <span className="btn-ico">
                      <IconExternal size={14} />
                    </span>
                    API docs
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
